export type Comparison = {
  comparisonId: string;
  connectionId: string;
  status: ComparisonStatusEnum;
  lastUpdate: string | null;
  insurerDataStatus: ComparisonStatusEnum;
  insurerDataLastUpdate: string | null;
  insurerDataRequestId: string | null;
};

export type ComparisonItem = {
  comparisonItemId: string;
  comparisonId: string;
  connectionId: string;
  status: ComparisonStatusEnum;
  employeeName: string;
  employeeBsn: string | null;
  itemType: ComparisonItemType;
  eventSource: ComparisonItemSourceFields;
  insurerSource: ComparisonItemSourceFields;
  discrepancies: Discrepancy[];
  duplicateInsurerSource: ComparisonItemSourceFields[];
  sourcesComparisonResult: SourcesComparisonResult;
};

export enum ComparisonStatusEnum {
  Pending = 'pending',
  Completed = 'completed',
  Processing = 'processing',
  Failed = 'failed',
}

export enum ComparisonItemType {
  NewEmployee = 'new-employee',
  ContractUpdated = 'contract-updated',
  EmployeeAddressUpdated = 'employee-address-updated',
  EmployeeInformationUpdated = 'employee-information-updated',
  EmployeeLeftCompany = 'employee-left-company',
  EmployeePartTimePercentageUpdated = 'employee-part-time-percentage-updated',
  GrossWageUpdated = 'gross-wage-updated',
  EmployeePartnerUpdated = 'employee-partner-updated',
  NewPeriodicWage = 'new-periodic-wage',
  NewParticipantBasis = 'new-participant-basis',
  MonthlyParticipant = 'monthly-participant',
  ReEmployed = 're-employed',
  NewParticipation = 'new-participation',
  EndParticipation = 'end-participation',
  Unknown = 'unknown',
}

export type ComparisonItemSourceFields = {
  id: string | null;
  externalId: string | null;
  employeeName: string | null;
  birthDate: string | null;
  gender: string | null;
  civilStatus: string | null;
  bsn: string | null;
  employmentStartDate: string | null;
  employmentEndDate: string | null;
  wage: number | null;
  partTimePercentage: number | null;
  startDateFullTimeAnnualWage: string | null;
  startDatePartTimePercentage: string | null;
  sourceType: string | null;
  logMissingData: Record<string, string>[] | null;
};

export type Discrepancy = {
  field: string;
  eventValue: DiscrepancyValue;
  insurerValue: DiscrepancyValue;
  newEventType: ComparisonItemType;
};

export type DiscrepancyValue = {
  type: DiscrepancyValueType;
  value: string | number | boolean | null;
};

export enum DiscrepancyValueType {
  Money = 'money',
  DateTime = 'datetime',
  Boolean = 'boolean',
  Float = 'float',
  String = 'string',
  Integer = 'integer',
}

export type ComparisonArchiveEventsRequest = {
  connectionId: string;
  eventIds: string[];
  selectAll: boolean;
};

export const fieldTypeMap: Record<
  keyof ComparisonItemSourceFields,
  'date' | 'currency' | 'percentage' | 'text' | 'boolean'
> = {
  id: 'text',
  externalId: 'text',
  employeeName: 'text',
  birthDate: 'date',
  gender: 'text',
  civilStatus: 'text',
  bsn: 'text',
  employmentStartDate: 'date',
  employmentEndDate: 'date',
  wage: 'currency',
  partTimePercentage: 'percentage',
  startDateFullTimeAnnualWage: 'date',
  startDatePartTimePercentage: 'date',
  sourceType: 'text',
  logMissingData: 'text',
};

export type CreateComparisonRequest = {
  comparisonId: string;
  connectionId: string;
  eventIds: string[];
  selectAll: boolean;
};

export type ComparisonItemsActionRequest = {
  comparisonId: string;
  connectionId: string;
  itemsIds: string[];
  selectAll: boolean;
};

export type SearchComparisonItemsQuery = {
  comparisonId: string | undefined;
  connectionId: string | undefined;
  page: number | null;
  pageSize: number | null;
  searchQuery: string | null;
  itemTypes: ComparisonItemType[] | null;
};

export type DownloadComparisonItemsRequest = ComparisonItemsActionRequest & {
  itemTypes?: ComparisonItemType[] | null;
  searchQuery?: string | null;
};

export type SearchComparisonItemsQueryResponse = {
  page: number | null;
  pageSize: number | null;
  totalItems: number;
  items: ComparisonItem[];
};

export type SearchComparisonsResponse = {
  page: number | null;
  pageSize: number | null;
  totalItems: number;
  items: Comparison[];
};

export type CreateEventFromDifferenceRequest = {
  comparisonId: string;
  connectionId: string;
  comparisonItemId: string;
  newEventType: ComparisonItemType;
};

export interface Task {
  taskId: string;
  connectionId: string;
  status: 'queued' | 'processing' | 'pending';
  moduleType: string;
}

export interface SearchTasksResponse {
  tasks: Task[];
  comparison: Comparison;
}

export enum SourcesComparisonResult {
  WithoutInsurerSource = 'without_insurer_source',
  WithoutSalarySource = 'without_salary_source',
  WithoutDiscrepancies = 'without_discrepancies',
  WithDiscrepancies = 'with_discrepancies',
}
